import { isValid, parse, startOfDay, subDays } from 'date-fns'

import { formatDate } from '@/lib/date/date'

import { DATE_FILTER_PRESETS } from '@/components/lemmala-design-system/table/consts'
import type {
  DateFilterModel,
  DateFilterPreset,
} from '@/components/lemmala-design-system/table/types'

export const parseDate = (value?: string) => {
  if (!value) return undefined
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed
}

/** Serializes a date into the `yyyy-MM-dd` form the ag-grid filter model expects. */
export const formatModelDate = (value: Date) => formatDate(value, 'yyyyMMdd')

// Local midnight: `new Date('2026-03-04')` would read it as UTC midnight and can land a day early.
export const parseModelDate = (value: string) => {
  const date = parse(value, 'yyyy-MM-dd', new Date())
  return isValid(date) ? date : undefined
}

export const formatRangeLabel = (
  dateFrom?: Date,
  dateTo?: Date,
  format: Parameters<typeof formatDate>[1] = 'monthDayYear',
) => {
  if (!dateFrom || !dateTo) return ''
  return `${formatDate(dateFrom, format)} - ${formatDate(dateTo, format)}`
}

export const isSameDay = (left?: Date, right?: Date) => {
  if (!left || !right) return false
  return left.toDateString() === right.toDateString()
}

export const orderDates = (left: Date, right: Date) => {
  return left <= right ? [left, right] : [right, left]
}

export const getMonthStart = (value?: Date) => {
  const date = value || new Date()
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

export const findDatePreset = (type?: string | null) =>
  DATE_FILTER_PRESETS.find(({ value }) => value === type)

// Today counts, so "Last 7 days" starts at midnight six days ago.
export const datePresetStart = (preset: DateFilterPreset, now = new Date()) =>
  startOfDay(subDays(now, (findDatePreset(preset)?.days ?? 1) - 1))

export const isDateFilterActive = (filter: DateFilterModel) =>
  !!findDatePreset(filter.type) || (!!filter.dateFrom && !!filter.dateTo)
